import React, { useState } from "react"

import { LightBulbIcon, XMarkIcon } from "@heroicons/react/24/outline"

import classNames from "src/classNames"

import MessageList from "./MessageList"
import {
  CopilotCreateSuggestionResult,
  CopilotEndpoint,
  CopilotResponse,
} from "./types"
import useCopilotConversation, {
  COPILOT_ACTIONS,
} from "./useCopilotConversation"

type CopilotViewProps = {
  responses?: CopilotResponse[]
  historyLoading: boolean
  fetching: boolean
  createSuggestion: (
    endpoint: CopilotEndpoint
  ) => Promise<CopilotCreateSuggestionResult>
  headerText: string
}

const CopilotView: React.FC<CopilotViewProps> = ({
  responses,
  historyLoading,
  fetching,
  createSuggestion,
  headerText,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { messages, createCopilotSuggestion } = useCopilotConversation(
    responses,
    createSuggestion
  )

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        className={classNames(
          "fixed bottom-6 right-6 z-50 flex items-center justify-center rounded-full shadow-lg transition hover:opacity-90",
          "bg-yellow-300 text-black",
          isOpen ? "h-16 w-16 bg-yellow-400" : "h-24 w-24"
        )}
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        {isOpen ? (
          <XMarkIcon className="h-8 w-8" aria-hidden />
        ) : (
          <LightBulbIcon className="h-10 w-10" aria-hidden />
        )}
      </button>

      {isOpen && (
        <div className="fixed bottom-24 right-6 z-40 flex h-[680px] w-[360px] flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl">
          <header className="flex-shrink-0 border-b border-neutral-200 bg-white px-4 py-3 font-semibold text-neutral-800">
            {headerText}
          </header>
          <MessageList messages={messages} copilotLabel="CoPilot" />
          {fetching && (
            <div className="flex-shrink-0 border-t border-neutral-100 bg-neutral-50 py-2 pl-6 pr-4 text-sm text-neutral-500">
              <span className="inline-flex items-center gap-1">
                <span className="animate-[pulse_1s_ease-in-out_infinite]">
                  Thinking
                </span>
                <span className="inline-flex gap-0.5">
                  <span className="h-1 w-1 animate-[pulse_1s_ease-in-out_infinite] rounded-full bg-neutral-400 [animation-delay:0ms]" />
                  <span className="h-1 w-1 animate-[pulse_1s_ease-in-out_infinite] rounded-full bg-neutral-400 [animation-delay:150ms]" />
                  <span className="h-1 w-1 animate-[pulse_1s_ease-in-out_infinite] rounded-full bg-neutral-400 [animation-delay:300ms]" />
                </span>
              </span>
            </div>
          )}
          <div className="relative z-10 flex-shrink-0 border-t border-neutral-200 bg-neutral-50 p-3">
            <div className="flex flex-wrap gap-2">
              {Object.values(COPILOT_ACTIONS).map(({ prompt, endpoint }) => (
                <button
                  type="button"
                  disabled={fetching || historyLoading}
                  onClick={() => createCopilotSuggestion(prompt, endpoint)}
                  className={
                    "rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 shadow-sm transition " +
                    "hover:border-neutral-400 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-60"
                  }
                  key={endpoint}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default CopilotView
